import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Eye, X, Calendar, Users, Percent, Clock3, CheckCircle2, XCircle, Filter, BellRing } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "../RessourcesStyles/ConsolidationDemandes.css";
import DemandeTrackingStepper from "./demandes/DemandeTrackingStepper";

import {
    clearDemandeTracking,
    downloadGenerateBilanCampagneExcelRequest,
    downloadGeneratePlanFormationExcelRequest,
    fetchParticipationCampagneRequest,
    getDemandeTrackingRequest,
    getCompanyDemandesRequest,
    prolongerCampagneRequest,
    relanceParticipationCampagneRequest,
    relanceWorkflowCampagneRequest
} from "../../../../redux/actions/demandesActions";
import {fetchVisibleCampagnesRequest} from "../../../../redux/actions/campagnesActions";
// ⚠️ adapte si ton projet a une action pour charger les campagnes

const norm = (v) => String(v ?? "").trim().toLowerCase();

const safeDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
};

const fmtDateFR = (iso) => {
    const d = safeDate(iso);
    if (!d) return "—";
    // DD/MM/YYYY
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    return `${dd}/${mm}/${yy}`;
};

const getStatusCode = (d) =>
    String(d?.rfStatus ?? d?.managerStatus ?? d?.status ?? "").trim().toUpperCase();

const statusLabel = (code) => {
    if (code === "APPROVED") return "Traitée";
    if (code === "REJECTED") return "Rejetée";
    if (code === "PENDING_RF") return "Validée par le manager";
    if (code === "PENDING_MANAGER") return "En cours";
    return code || "—";
};

const statusClass = (code) => {
    if (code === "APPROVED") return "st-treated";
    if (code === "REJECTED") return "st-rejected";
    if (code === "PENDING_RF") return "st-manager";
    if (code === "PENDING_MANAGER") return "st-pending";
    return "st-default";
};

const getReference = (d) =>
    d?.reference || d?.formationReference || d?.formationRef || d?.ref || "—";

const getActionDemande = (d) =>
    d?.formationNom || d?.formationName || d?.action || d?.description || "—";

const getRubrique = (d) =>
    d?.domaineNom || d?.domain || d?.Ddomain || "—";

const uniqBy = (arr, keyFn) => {
    const map = new Map();
    arr.forEach((x) => {
        const k = keyFn(x);
        if (!map.has(k)) map.set(k, x);
    });
    return [...map.values()];
};

const statusPillClass = (status) => {
    if (status === "APPROVED") return "pill pill-green";
    if (status === "REJECTED") return "pill pill-red";
    if (status === "PENDING_RF") return "pill pill-blue";
    if (status === "PENDING_MANAGER") return "pill pill-orange";
    return "pill pill-gray";
};

const statusPillLabel = (status) => {
    if (status === "APPROVED") return "✓ Traitée";
    if (status === "REJECTED") return "✗ Rejetée";
    if (status === "PENDING_RF") return "⏳ Validée manager";
    if (status === "PENDING_MANAGER") return "◌ En cours";
    return status || "—";
};

const DetailField = ({ label, value, highlight }) => (
    <div className="detailField">
        <div className="detailFieldLabel">{label}</div>
        <div className={`detailFieldValue ${highlight ? "detailFieldHighlight" : ""}`}>
            {value ?? <span className="detailNull">—</span>}
        </div>
    </div>
);

const DetailSection = ({ title, icon, children }) => (
    <div className="detailSection">
        <div className="detailSectionTitle">
            <span className="detailSectionIcon">{icon}</span>
            {title}
        </div>
        <div className="detailSectionGrid">{children}</div>
    </div>
);

const DemandeDetailView = ({ data: d, tracking, loadingTracking, onNavigate }) => {
    if (!d) return null;
    const st = String(d.status ?? "").trim().toUpperCase();
    const fmtDt = (v) => {
        if (!v) return null;
        const dt = new Date(v);
        if (isNaN(dt)) return v;
        return dt.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    };

    return (
        <div className="demandeDetailWrap">
            {/* Header hero */}
            <div className="detailHero">
                <div className="detailHeroLeft">
                    <div className="detailHeroAvatar">
                        {(d.targetUserName || "?")[0].toUpperCase()}
                    </div>
                    <div>
                        <div className="detailHeroName">{d.targetUserName || "—"}</div>
                        <div className="detailHeroSub">
                            {d.formationNom || d.formationName || "—"}
                            {d.campagneNom ? <span className="detailHeroCampagne"> · {d.campagneNom}</span> : null}
                        </div>
                    </div>
                </div>
                <div className={statusPillClass(st)}>{statusPillLabel(st)}</div>
            </div>

            {/* Sections */}
            <DetailSection title="Formation" icon="🎓">
                <DetailField label="Nom de la formation" value={d.formationNom || d.formationName} highlight />
                <DetailField label="Campagne" value={d.campagneNom} />
                <DetailField label="Domaine" value={d.domaineNom} />
                <DetailField label="Thème" value={d.themeNom} />
                <DetailField label="Mode" value={d.mode} />
                <DetailField label="Période" value={d.period} />
                <DetailField label="Niveau" value={d.niveau} />
                <DetailField label="Priorité" value={d.priorite != null ? `P${d.priorite}` : null} />
                <DetailField label="Certifiante" value={d.certifiante != null ? (d.certifiante ? "Oui" : "Non") : null} />
            </DetailSection>

            <DetailSection title="Collaborateur & Manager" icon="👤">
                <DetailField label="Collaborateur" value={d.targetUserName} highlight />
                <DetailField label="Manager" value={d.managerName} />
                <DetailField label="Créé par" value={d.createdByName} />
            </DetailSection>

            <DetailSection title="Suivi & Décisions" icon="📋">
                <DetailField label="Statut global" value={statusPillLabel(st)} />
                <DetailField label="Statut manager" value={d.managerStepStatus} />
                <DetailField label="Statut RF" value={d.rfStepStatus} />
                <DetailField label="Décision manager par" value={d.managerDecisionByName} />
                <DetailField label="Décision RF par" value={d.rfDecisionByName} />
                <DetailField label="Commentaire manager" value={d.managerComment} />
                <DetailField label="Commentaire RF" value={d.rfComment} />
            </DetailSection>

            <DetailSection title="Dates" icon="📅">
                <DetailField label="Créé le" value={fmtDt(d.createdAt)} />
                <DetailField label="Modifié le" value={fmtDt(d.updatedAt)} />
                <DetailField label="Décision manager le" value={fmtDt(d.managerDecisionAt)} />
                <DetailField label="Décision RF le" value={fmtDt(d.rfDecisionAt)} />
            </DetailSection>
            <DetailSection title="Suivi de la demande" icon="📅">
                {loadingTracking ? (
                    <div className="detailNull">Chargement du suivi...</div>
                ) : tracking ? (
                    <DemandeTrackingStepper tracking={tracking} onNavigate={onNavigate} />
                ) : (
                    <div className="detailNull">Aucun suivi disponible.</div>
                )}
            </DetailSection>
        </div>
    );
};
const ModalShell = ({ open, title, children, onClose, footer }) => {
    if (!open) return null;
    return (
        <div className="consModal" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
            <div className="consModalPanel">
                <div className="consModalHeader">
                    <div>
                        <div className="consModalTitle">{title}</div>
                    </div>
                    <button className="iconClose" type="button" onClick={onClose} title="Fermer">
                        <X size={18} />
                    </button>
                </div>
                <div className="consModalBody">{children}</div>
                {footer ? <div className="consModalFooter">{footer}</div> : null}
            </div>
        </div>
    );
};

export default function ConsolidationList() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // ✅ demandes (nouveau projet)
    const demandesState = useSelector((s) => s.demandes || {});
    const companyDemandes = demandesState.companyDemandes;
    const loading = !!(demandesState.loadingCompanyDemandes || demandesState.loading);
    const selectedDemandeTracking = demandesState.selectedDemandeTracking;
    const loadingTracking = !!demandesState.loadingTracking;
    const participationByCampagneId = demandesState.participationByCampagneId || {};
    const loadingRelance = !!demandesState.loadingRelance;
    const loadingProlongation = !!demandesState.loadingProlongation;

    const campagnesState = useSelector((s) => s.campagnes || {});
    const campagnesRaw = campagnesState.visibleList;
    const campagnes = useMemo(() => {
        const formationOnly = (campagnesRaw || []).filter(
            (c) => String(c?.typeCampagne || "").toUpperCase() === "FORMATION"
        );
        return [...formationOnly].sort((a, b) => {
            if (a?.status === b?.status) return 0;
            return a?.status === true ? -1 : 1;
        });
    }, [campagnesRaw]);
    const activeFormationCampagnes = useMemo(
        () => campagnes.filter((c) => c?.status === true),
        [campagnes]
    );

    useEffect(() => {
        dispatch(getCompanyDemandesRequest());
        dispatch(fetchVisibleCampagnesRequest());
    }, [dispatch]);

    // -------- Campagne selection
    const [campagneId, setCampagneId] = useState("");

    useEffect(() => {
        if (!campagnes.length) {
            if (campagneId) setCampagneId("");
            return;
        }

        const stillInList = campagnes.some((c) => String(c?.id) === String(campagneId));
        if (campagneId && !stillInList) {
            setCampagneId("");
            return;
        }

        if (!campagneId && activeFormationCampagnes.length) {
            const defaultCampagne = activeFormationCampagnes.find((c) => c?.isDefault) || activeFormationCampagnes[0];
            setCampagneId(String(defaultCampagne?.id ?? ""));
        }
    }, [campagnes, activeFormationCampagnes, campagneId]);

    useEffect(() => {
        if (campagneId) {
            dispatch(fetchParticipationCampagneRequest(Number(campagneId)));
        }
    }, [dispatch, campagneId]);

    const selectedCampagne = useMemo(() => {
        if (!campagneId) return null;
        return (campagnes || []).find((c) => String(c?.id) === String(campagneId)) || null;
    }, [campagnes, campagneId]);

    // -------- Dates non modifiables (issues de la campagne)
    // Adapte les noms si besoin: startDate/endDate, dateDebut/dateFin, from/to...
    const campagneStart =
        selectedCampagne?.dateDebut ||
        selectedCampagne?.startDate ||
        selectedCampagne?.from ||
        selectedCampagne?.start ||
        null;

    const campagneEnd =
        selectedCampagne?.dateFin ||
        selectedCampagne?.endDate ||
        selectedCampagne?.to ||
        selectedCampagne?.end ||
        null;

    // -------- Filtres box (comme ancien)
    const [showFilters, setShowFilters] = useState(false);
    const [nom, setNom] = useState("");
    const [reference, setReference] = useState("");
    const [actionDemande, setActionDemande] = useState("");
    const [statut, setStatut] = useState("");
    const [dateDemande, setDateDemande] = useState("");
    const [dateTraitement, setDateTraitement] = useState("");

    const resetFilters = () => {
        setNom("");
        setReference("");
        setActionDemande("");
        setStatut("");
        setDateDemande("");
        setDateTraitement("");
    };

    // Compter le nombre de filtres actifs
    const activeFiltersCount = useMemo(() => {
        let count = 0;
        if (nom) count++;
        if (reference) count++;
        if (actionDemande) count++;
        if (statut) count++;
        if (dateDemande) count++;
        if (dateTraitement) count++;
        return count;
    }, [nom, reference, actionDemande, statut, dateDemande, dateTraitement]);

    // -------- Pagination
    const PAGE_SIZE = 10;
    const [page, setPage] = useState(1);

    const [demandesEnCoursPage, setDemandesEnCoursPage] = useState(1);
    const [actionsValideesPage, setActionsValideesPage] = useState(1);
    const [demandesRejeteesPage, setDemandesRejeteesPage] = useState(1);
    const [participantsPage, setParticipantsPage] = useState(1);
    const itemsPerPage = 5;

    // -------- Modals
    const [openUsersModal, setOpenUsersModal] = useState(false);
    const [openParticipationModal, setOpenParticipationModal] = useState(false);
    const [openStatsModal, setOpenStatsModal] = useState(false);
    const [statsModalTitle, setStatsModalTitle] = useState("");
    const [statsModalRows, setStatsModalRows] = useState([]);

    const [openProlonger, setOpenProlonger] = useState(false);
    const [openRelancer, setOpenRelancer] = useState(false);

    const [selectedRow, setSelectedRow] = useState(null);

    useEffect(() => {
        if (!selectedRow?.id) {
            dispatch(clearDemandeTracking());
            return;
        }

        dispatch(getDemandeTrackingRequest(selectedRow.id));

        return () => {
            dispatch(clearDemandeTracking());
        };
    }, [dispatch, selectedRow]);

    // -------- Data format
    const formatted = useMemo(() => {
        return (companyDemandes || []).map((d) => {
            const st = getStatusCode(d);
            return {
                id: d?.id,
                raw: d,
                campagneId: d?.campagneId,
                name: d?.targetUserName || "—",
                targetUserId: d?.targetUserId ?? null,
                freference: getReference(d),
                formation: getActionDemande(d),
                domain: getRubrique(d),
                dStatus: statusLabel(st),
                statusCode: st,
                dDemande: d?.createdAt || d?.updatedAt || null,
                dTraitement: d?.rfDecisionAt || d?.managerDecisionAt || null,
            };
        });
    }, [companyDemandes]);


    // -------- Filtre par campagne (obligatoire)
    const byCampagne = useMemo(() => {
        if (!campagneId) return [];
        return formatted.filter((x) => String(x.campagneId) === String(campagneId));
    }, [formatted, campagneId]);

    const filtered = useMemo(() => {
        let out = byCampagne;

        if (nom) out = out.filter((x) => norm(x.name).includes(norm(nom)));
        if (reference) out = out.filter((x) => norm(x.freference).includes(norm(reference)));
        if (actionDemande) out = out.filter((x) => norm(x.formation).includes(norm(actionDemande)));
        if (statut) out = out.filter((x) => norm(x.dStatus).includes(norm(statut)));

        if (dateDemande) {
            const f = new Date(`${dateDemande}T00:00:00`);
            out = out.filter((x) => {
                const d = safeDate(x.dDemande);
                return d && d >= f;
            });
        }

        if (dateTraitement) {
            const f = new Date(`${dateTraitement}T00:00:00`);
            out = out.filter((x) => {
                const d = safeDate(x.dTraitement);
                return d && d >= f;
            });
        }

        out = [...out].sort((a, b) => {
            const da = safeDate(a.dDemande)?.getTime() || 0;
            const db = safeDate(b.dDemande)?.getTime() || 0;
            return db - da;
        });

        return out;
    }, [byCampagne, nom, reference, actionDemande, statut, dateDemande, dateTraitement]);

    useEffect(() => {
        setPage(1);
        setDemandesEnCoursPage(1);
        setActionsValideesPage(1);
        setDemandesRejeteesPage(1);
        setParticipantsPage(1);
    }, [campagneId, nom, reference, actionDemande, statut, dateDemande, dateTraitement]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageSafe = Math.min(Math.max(page, 1), totalPages);
    const pageItems = filtered.slice((pageSafe - 1) * PAGE_SIZE, (pageSafe - 1) * PAGE_SIZE + PAGE_SIZE);

    // -------- Stats (cliquables)
    const demandesEncours = filtered.filter((d) => d.statusCode === "PENDING_RF" || d.statusCode === "PENDING_MANAGER");
    const demandesValidees = filtered.filter((d) =>  d.statusCode === "APPROVED");
    const demandesRejetees = filtered.filter((d) => d.statusCode === "REJECTED");

    const paginatedDemandesEnCours = useMemo(() => {
        return demandesEncours.slice(
            (demandesEnCoursPage - 1) * itemsPerPage,
            demandesEnCoursPage * itemsPerPage
        );
    }, [demandesEncours, demandesEnCoursPage]);

    const paginatedActionsValidees = useMemo(() => {
        return demandesValidees.slice(
            (actionsValideesPage - 1) * itemsPerPage,
            actionsValideesPage * itemsPerPage
        );
    }, [demandesValidees, actionsValideesPage]);

    const paginatedDemandesRejetees = useMemo(() => {
        return demandesRejetees.slice(
            (demandesRejeteesPage - 1) * itemsPerPage,
            demandesRejeteesPage * itemsPerPage
        );
    }, [demandesRejetees, demandesRejeteesPage]);

    useEffect(() => {
        const total = Math.ceil(demandesEncours.length / itemsPerPage) || 1;
        if (demandesEnCoursPage > total) {
            setDemandesEnCoursPage(total);
        }
    }, [demandesEncours.length, demandesEnCoursPage]);

    useEffect(() => {
        const total = Math.ceil(demandesValidees.length / itemsPerPage) || 1;
        if (actionsValideesPage > total) {
            setActionsValideesPage(total);
        }
    }, [demandesValidees.length, actionsValideesPage]);

    useEffect(() => {
        const total = Math.ceil(demandesRejetees.length / itemsPerPage) || 1;
        if (demandesRejeteesPage > total) {
            setDemandesRejeteesPage(total);
        }
    }, [demandesRejetees.length, demandesRejeteesPage]);
    const participation = campagneId ? participationByCampagneId[String(campagneId)] : null;
    const participantsSet = useMemo(() => {
        const s = new Set();
        filtered.forEach((d) => {
            if (d.targetUserId != null) s.add(String(d.targetUserId));
            else if (d.name) s.add(d.name);
        });
        return s;
    }, [filtered]);
    const participants = participation?.participantsCount ?? participantsSet.size;


    // -------- Collaborateurs concernés = uniques depuis demandes de la campagne
    const concernedUsers = useMemo(() => {
        if (Array.isArray(participation?.eligibleUsers)) {
            return participation.eligibleUsers.map((u) => ({
                id: u.id,
                name: u.fullName || `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || `Utilisateur ${u.id}`,
                targetUserId: u.id,
            }));
        }
        const base = filtered.map((d) => ({
            id: d.targetUserId ?? d.name,
            name: d.name,
            targetUserId: d.targetUserId,
        }));
        return uniqBy(base, (u) => String(u.id));
    }, [filtered, participation]);
    const participantUsers = useMemo(() => {
        if (Array.isArray(participation?.participants)) {
            return participation.participants.map((u) => ({
                id: u.id,
                name: u.fullName || `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || `Utilisateur ${u.id}`,
                targetUserId: u.id,
            }));
        }
        return concernedUsers;
    }, [participation, concernedUsers]);

    const paginatedParticipants = useMemo(() => {
        return participantUsers.slice(
            (participantsPage - 1) * itemsPerPage,
            participantsPage * itemsPerPage
        );
    }, [participantUsers, participantsPage]);

    useEffect(() => {
        const total = Math.ceil(participantUsers.length / itemsPerPage) || 1;
        if (participantsPage > total) {
            setParticipantsPage(total);
        }
    }, [participantUsers.length, participantsPage]);

    const activeList = useMemo(() => {
        if (statsModalTitle === "Demandes en cours") {
            return {
                paginated: paginatedDemandesEnCours,
                page: demandesEnCoursPage,
                setPage: setDemandesEnCoursPage,
                total: demandesEncours.length,
                label: "demandes en cours",
                isRequests: true
            };
        }
        if (statsModalTitle === "Actions validées") {
            return {
                paginated: paginatedActionsValidees,
                page: actionsValideesPage,
                setPage: setActionsValideesPage,
                total: demandesValidees.length,
                label: "actions validées",
                isRequests: true
            };
        }
        if (statsModalTitle === "Demandes rejetées") {
            return {
                paginated: paginatedDemandesRejetees,
                page: demandesRejeteesPage,
                setPage: setDemandesRejeteesPage,
                total: demandesRejetees.length,
                label: "demandes rejetées",
                isRequests: true
            };
        }
        if (statsModalTitle === "Participants") {
            return {
                paginated: paginatedParticipants,
                page: participantsPage,
                setPage: setParticipantsPage,
                total: participantUsers.length,
                label: "participants",
                isRequests: false
            };
        }
        return null;
    }, [
        statsModalTitle,
        paginatedDemandesEnCours, demandesEnCoursPage, demandesEncours.length,
        paginatedActionsValidees, actionsValideesPage, demandesValidees.length,
        paginatedDemandesRejetees, demandesRejeteesPage, demandesRejetees.length,
        paginatedParticipants, participantsPage, participantUsers.length
    ]);

    const renderPagination = ({ totalItems, currentPage, setCurrentPage, label }) => {
        const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

        if (!totalItems || totalItems <= itemsPerPage) return null;

        return (
            <div className="cicada-pagination">
                <div className="cicada-pagination-info">
                    Affichage de {(currentPage - 1) * itemsPerPage + 1}{' '}
                    à {Math.min(currentPage * itemsPerPage, totalItems)}{' '}
                    sur {totalItems} {label}
                </div>

                <div className="cicada-pagination-actions">
                    <button
                        type="button"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    >
                        Précédent
                    </button>

                    <span>
                        Page {currentPage} / {totalPages}
                    </span>

                    <button
                        type="button"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    >
                        Suivant
                    </button>
                </div>
            </div>
        );
    };

    // -------- Taux participation (comme ancien) :
    // On essaye d'utiliser une population de campagne si elle existe, sinon on calcule sur concernés.
    const populationCount =
        participation?.eligibleCount ??
        (selectedCampagne?.populationCount ||
        selectedCampagne?.usersCount ||
        selectedCampagne?.collaborateursCount ||
        selectedCampagne?.effectif ||
        null);

    const fallbackParticipationRate = useMemo(() => {
        const denom = populationCount != null ? Number(populationCount) : Number(concernedUsers.length);
        const num = Number(participants);
        if (!denom || denom <= 0) return null;
        const pct = Math.round((num / denom) * 100);
        return Math.min(100, Math.max(0, pct));
    }, [populationCount, concernedUsers.length, participants]);
    const participationRate = participation?.participationRate ?? fallbackParticipationRate;
    const notParticipatedCount = participation?.notParticipatedUsers?.length ?? Math.max(0, Number(populationCount || 0) - Number(participants || 0));

    // -------- open details helpers
    const openStatsDetails = (title, rows) => {
        setStatsModalTitle(title);
        setStatsModalRows(rows);
        setOpenStatsModal(true);
    };

    // -------- Actions: Prolonger / Relancer (modal)
    const [newEndDate, setNewEndDate] = useState("");
    const [prolongerComment, setProlongerComment] = useState("");
    const downloadTemplateBilan = () => {
        if (!campagneId) return;
        dispatch(downloadGenerateBilanCampagneExcelRequest(Number(campagneId)));
    };
    const downloadTemplatePlan = () => {
        if (!campagneId) return;
        dispatch(downloadGeneratePlanFormationExcelRequest(Number(campagneId)));
    };
    const submitProlonger = () => {
        if (!campagneId) return;
        dispatch(prolongerCampagneRequest({
            campagneId: Number(campagneId),
            newEndDate,
            comment: prolongerComment,
            onSuccess: () => {
                setOpenProlonger(false);
                setNewEndDate("");
                setProlongerComment("");
            },
        }));
    };

    const submitRelancer = () => {
        if (!campagneId) return;
        dispatch(relanceParticipationCampagneRequest({
            campagneId: Number(campagneId),
            onSuccess: () => setOpenRelancer(false),
        }));
    };

    const handleRelanceWorkflowCampagne = async (event) => {
        event?.stopPropagation?.();
        const result = await Swal.fire({
            icon: "question",
            title: "",
            text: "Voulez-vous envoyer un email de relance aux validateurs pour traiter leurs demandes en attente ?",
            showCancelButton: true,
            confirmButtonText: "Confirmer",
            cancelButtonText: "Annuler",
        });
        if (result.isConfirmed) {
            if (!campagneId) return;
            dispatch(relanceWorkflowCampagneRequest({
                campagneId: Number(campagneId),
            }));
        }
    };

    return (
        <div className="consPage">
            <h2>Consolidation</h2>
            <div className="consRow0">
                <div className="consCampagneSelect">
                        <select value={campagneId} onChange={(e) => setCampagneId(e.target.value)} disabled={!campagnes?.length}>
                            {!campagnes?.length && (
                                <option value="">Aucune campagne formation active</option>
                            )}
                            {(campagnes || []).map((c) => (
                                <option key={c.id} value={String(c.id)}>
                                    {c.nom || c.name || `Campagne ${c.id}`}
                                </option>
                        ))}
                    </select>
                </div>

                <div className="consRightPanel">
                    <div className="consRightRow">
                        <div className="consRightLabel">Collaborateurs Concernés</div>
                        <button className="chipBtn" type="button" onClick={() => setOpenUsersModal(true)}>
                            TOUS LES COLLABORATEURS
                        </button>
                    </div>

                    <div className="consRightRow">
                        <div className="consRightLabel">Taux de Participation</div>
                        <button
                            type="button"
                            className={`rateBadge ${participationRate == null ? "is-empty" : participationRate < 30 ? "is-low" : participationRate < 70 ? "is-mid" : "is-high"}`}
                            onClick={() => setOpenParticipationModal(true)}
                            title="Voir détails participation"
                        >
                            {participationRate == null ? "—" : `${participationRate}%`}
                        </button>
                    </div>
                </div>
            </div>

            {/* Row 1: Dates + boutons */}
            <div className="consTop">
                <div className="consRange">
                    <Calendar size={18} />
                    <span>Du : </span>
                    <input value={campagneStart ? fmtDateFR(campagneStart).split("/").reverse().join("-") : ""} readOnly disabled />
                    <span className="toTxt">au :</span>
                    <input value={campagneEnd ? fmtDateFR(campagneEnd).split("/").reverse().join("-") : ""} readOnly disabled />
                </div>

                <div className="consTopBtns">
                            <button className="btnTop btnDark" type="button" onClick={downloadTemplateBilan} disabled={!campagneId}>Extraire le bilan</button>
                            <button className="btnTop btnDark" type="button" onClick={downloadTemplatePlan} disabled={!campagneId}>Extraire le plan de formation</button>
                            <button className="btnTop btnRed" type="button" onClick={() => setOpenProlonger(true)} disabled={!campagneId}>Prolonger</button>
                            <button className="btnTop btnBlue" type="button" onClick={() => setOpenRelancer(true)} disabled={!campagneId}>Relancer</button>
                </div>
            </div>

            {/* Stats (cliquables) */}
            <div className="consStats">
                            <div
                                className="statItem statBtn statWithAction"
                                role="button"
                                tabIndex={0}
                                onClick={() => openStatsDetails("Demandes en cours", demandesEncours)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") openStatsDetails("Demandes en cours", demandesEncours);
                                }}
                            >
                                <div>
                                    <div className="statNum cBlue">{demandesEncours.length}</div>
                                    <div className="statTxt"><Clock3 size={16} className="cBlue" />Demandes en cours</div>
                                </div>
                                <button
                                    className="relanceBtn statRelanceBtn"
                                    type="button"
                                    onClick={handleRelanceWorkflowCampagne}
                                    disabled={!campagneId || loadingRelance || demandesEncours.length === 0}
                                    title="Relancer les validateurs"
                                >
                                    <BellRing size={18} />
                                </button>
                            </div>

                <button
                    type="button"
                    className="statItem statBtn"
                    onClick={() => openStatsDetails("Actions validées", demandesValidees)}
                >
                    <div className="statNum cGreen">{demandesValidees.length}</div>
                    <div className="statTxt"><CheckCircle2 size={16} className="cGreen" />Actions validées</div>
                </button>

                <button
                    type="button"
                    className="statItem statBtn"
                    onClick={() => openStatsDetails("Demandes rejetées", demandesRejetees)}
                >
                    <div className="statNum cRed">{demandesRejetees.length}</div>
                    <div className="statTxt"><XCircle size={16} className="cRed" />Demandes rejetées</div>
                </button>

                <button
                    type="button"
                    className="statItem statBtn"
                    onClick={() => openStatsDetails("Participants", participantUsers)}
                    title="Liste des participants"
                >
                    <div className="statNum cYellow">{participants}</div>
                    <div className="statTxt"><Users size={16} className="cYellow" />Participants</div>
                </button>
            </div>

            {/* Bouton toggle filtres */}
            <div className="consFiltersToggle">
                <button
                    className="btnToggleFilters"
                    type="button"
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <Filter size={16} />
                    {showFilters ? "Masquer les filtres de recherche" : "Afficher les filtres de recherche"}
                    {activeFiltersCount > 0 && (
                        <span className="filterCount">{activeFiltersCount}</span>
                    )}
                </button>
            </div>

            {/* Filtres */}
            {showFilters && (
                <div className="consFiltersBox">
                    <div className="filtersHeaderLine">
                        <div className="line" />
                        <div className="filtersTitle">Filtres de recherche</div>
                        <div className="line lineBlue" />
                    </div>

                    <div className="filtersGrid">
                        <div className="fField">
                            <label>Collaborateur</label>
                            <input value={nom} onChange={(e) => setNom(e.target.value)} />
                        </div>

                        <div className="fField">
                            <label>Référence</label>
                            <input value={reference} onChange={(e) => setReference(e.target.value)} />
                        </div>

                        <div className="fField">
                            <label>Action demandée</label>
                            <input value={actionDemande} onChange={(e) => setActionDemande(e.target.value)} />
                        </div>

                        <div className="fField">
                            <label>Statut de la demande</label>
                            <select value={statut} onChange={(e) => setStatut(e.target.value)}>
                                <option value=""></option>
                                <option value="En cours">En cours</option>
                                <option value="Validée par le manager">Validée par le manager</option>
                                <option value="Traitée">Traitée</option>
                                <option value="Rejetée">Rejetée</option>
                            </select>
                        </div>

                        <div className="fField">
                            <label>Date de la demande</label>
                            <input type="date" value={dateDemande} onChange={(e) => setDateDemande(e.target.value)} />
                        </div>

                        <div className="fField">
                            <label>Date du traitement</label>
                            <input type="date" value={dateTraitement} onChange={(e) => setDateTraitement(e.target.value)} />
                        </div>
                    </div>

                    <div className="filtersReset">
                        <button className="btnReset" type="button" onClick={resetFilters}>
                            ⟲ Réinitialiser les valeurs
                        </button>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="consTableWrap">
                <table className="consTable">
                    <thead>
                    <tr>
                        <th>Collaborateur</th>
                        <th>Référence</th>
                        <th>Action demandée</th>
                        <th>Domaine</th>
                        <th className="center">Statut</th>
                        <th className="center">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                            {!campagneId ? (
                                <tr><td colSpan={6} className="emptyCell">Aucune campagne de formation active disponible.</td></tr>
                    ) : loading ? (
                        <tr><td colSpan={6} className="emptyCell">Chargement...</td></tr>
                    ) : pageItems.length === 0 ? (
                        <tr><td colSpan={6} className="emptyCell">Aucune donnée.</td></tr>
                    ) : (
                        pageItems.map((row) => (
                            <tr key={row.id}>
                                <td>{row.name}</td>
                                <td>{row.freference}</td>
                                <td>{row.formation}</td>
                                <td>{row.domain}</td>
                                <td className={`center ${statusClass(row.statusCode)}`}>{row.dStatus}</td>
                                <td className="center">
                                    <button className="eyeBtn" type="button" onClick={() => setSelectedRow(row.raw)} title="Afficher">
                                        <Eye size={18} />
                                    </button>
                                </td>
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>

                <div className="pager">
                    <button className="btnPager" disabled={pageSafe <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                        Précédent
                    </button>
                    <div className="pagerInfo">
                        Page <b>{pageSafe}</b> / {totalPages} — <span className="muted">{filtered.length} demandes</span>
                    </div>
                    <button className="btnPager" disabled={pageSafe >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                        Suivant
                    </button>
                </div>
            </div>


            {/* Modal: Voir demande (œil) — VERSION AMÉLIORÉE */}
            {selectedRow && (
                <ModalShell
                    open={!!selectedRow}
                    title={`Détails demande #${selectedRow?.id ?? "—"}`}
                    onClose={() => setSelectedRow(null)}
                    footer={<button className="btnPager" type="button" onClick={() => setSelectedRow(null)}>Fermer</button>}
                >
                    <DemandeDetailView
                        data={selectedRow}
                        tracking={selectedDemandeTracking}
                        loadingTracking={loadingTracking}
                        onNavigate={(targetUrl) => {
                            if (targetUrl) navigate(targetUrl);
                        }}
                    />
                </ModalShell>
            )}

            {/* Modal: Collaborateurs concernés */}
            <ModalShell
                open={openUsersModal}
                title="Collaborateurs Concernés"
                onClose={() => setOpenUsersModal(false)}
                footer={<button className="btnPager" type="button" onClick={() => setOpenUsersModal(false)}>Fermer</button>}
            >
                <div className="miniNote">
                    Campagne : <b>{selectedCampagne?.nom || selectedCampagne?.name || campagneId || "—"}</b> — Total : <b>{concernedUsers.length}</b>
                </div>

                <div className="miniTableWrap">
                    <table className="miniTable">
                        <thead>
                        <tr>
                            <th>Collaborateur</th>
                            <th className="center">Demandes</th>
                            <th className="center">En cours</th>
                            <th className="center">Validées</th>
                            <th className="center">Rejetées</th>
                        </tr>
                        </thead>
                        <tbody>
                        {concernedUsers.map((u) => {
                            const rows = filtered.filter((d) => String(d.targetUserId ?? d.name) === String(u.id));
                            const enc = rows.filter((r) => r.statusCode === "PENDING_MANAGER").length;
                            const val = rows.filter((r) => r.statusCode === "PENDING_RF" || r.statusCode === "APPROVED").length;
                            const rej = rows.filter((r) => r.statusCode === "REJECTED").length;
                            return (
                                <tr key={String(u.id)}>
                                    <td>{u.name}</td>
                                    <td className="center">{rows.length}</td>
                                    <td className="center">{enc}</td>
                                    <td className="center">{val}</td>
                                    <td className="center">{rej}</td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            </ModalShell>

            {/* Modal: Taux de participation */}
            <ModalShell
                open={openParticipationModal}
                title="Taux de Participation"
                onClose={() => setOpenParticipationModal(false)}
                footer={<button className="btnPager" type="button" onClick={() => setOpenParticipationModal(false)}>Fermer</button>}
            >
                <div className="participationBox">
                    <div className="participationRow">
                        <div className="pLeft"><Users size={16} /> Participants</div>
                        <div className="pRight"><b>{participants}</b></div>
                    </div>
                    <div className="participationRow">
                        <div className="pLeft"><Percent size={16} /> Population campagne</div>
                        <div className="pRight">
                            <b>{populationCount != null ? populationCount : concernedUsers.length}</b>
                        </div>
                    </div>
                    <div className="participationRow">
                        <div className="pLeft"><Percent size={16} /> Taux</div>
                        <div className="pRight">
                            <span className="rateBig">{participationRate == null ? "—" : `${participationRate}%`}</span>
                        </div>
                    </div>


                </div>
            </ModalShell>

            {/* Modal: Stats cliquables (détails) */}
            <ModalShell
                open={openStatsModal}
                title={statsModalTitle}
                onClose={() => setOpenStatsModal(false)}
                footer={<button className="btnPager" type="button" onClick={() => setOpenStatsModal(false)}>Fermer</button>}
            >
                <div className="miniTableWrap">
                    {activeList && activeList.isRequests ? (
                        <table className="miniTable">
                            <thead>
                            <tr>
                                <th>Collaborateur</th>
                                <th>Référence</th>
                                <th>Action demandée</th>
                                <th>Domaine</th>
                                <th className="center">Statut</th>
                            </tr>
                            </thead>
                            <tbody>
                            {activeList.paginated.map((r) => (
                                <tr key={r.id}>
                                    <td>{r.name}</td>
                                    <td>{r.freference}</td>
                                    <td>{r.formation}</td>
                                    <td>{r.domain}</td>
                                    <td className={`center ${statusClass(r.statusCode)}`}>{r.dStatus}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    ) : (
                        // cas Participants (list users)
                        <table className="miniTable">
                            <thead>
                            <tr>
                                <th>Collaborateur</th>
                            </tr>
                            </thead>
                            <tbody>
                            {activeList ? activeList.paginated.map((u) => (
                                <tr key={String(u.id ?? u.name)}>
                                    <td>{u.name || String(u)}</td>
                                </tr>
                            )) : null}
                            </tbody>
                        </table>
                    )}
                </div>
                {activeList && renderPagination({
                    totalItems: activeList.total,
                    currentPage: activeList.page,
                    setCurrentPage: activeList.setPage,
                    label: activeList.label
                })}
            </ModalShell>

            {/* Modal: Prolonger */}
            <ModalShell
                open={openProlonger}
                title="Prolonger la campagne"
                onClose={() => setOpenProlonger(false)}
                footer={
                    <>
                        <button className="btnPager" type="button" onClick={() => setOpenProlonger(false)}>Annuler</button>
                            <button className="btnTop btnRed" type="button" onClick={submitProlonger} disabled={!campagneId || loadingProlongation}>Confirmer</button>
                    </>
                }
            >
                <div className="formGrid2">
                    <div className="fField">
                        <label>Nouvelle date de fin</label>
                        <input type="date" value={newEndDate} onChange={(e) => setNewEndDate(e.target.value)} />
                    </div>
                    <div className="fField">
                        <label>Commentaire</label>
                        <input value={prolongerComment} onChange={(e) => setProlongerComment(e.target.value)} placeholder="Optionnel..." />
                    </div>
                </div>
                <div className="miniNote">
                    Les personnes éligibles à cette campagne seront notifiées.
                </div>
            </ModalShell>

            {/* Modal: Relancer */}
            <ModalShell
                open={openRelancer}
                title="Relancer"
                onClose={() => setOpenRelancer(false)}
                footer={
                    <>
                        <button className="btnPager" type="button" onClick={() => setOpenRelancer(false)}>Annuler</button>
                            <button className="btnTop btnBlue" type="button" onClick={submitRelancer} disabled={!campagneId || loadingRelance}>Envoyer</button>
                    </>
                }
            >
                <div className="miniNote">
                    Cette action relancera les personnes éligibles à cette campagne qui n’ont pas encore effectué de demande de formation.
                    {participation ? <> <b>{notParticipatedCount}</b> personne(s) concernée(s).</> : null}
                </div>
            </ModalShell>
        </div>
    );
}
